import i18n from 'services/i18n';
import { IFormElementProps, ErrorResult } from 'models/form';
import { isBoolean, isObject, isString } from 'lodash-es';
import { FieldInputProps, FieldMetaState } from 'react-final-form';

export const submitForm = (formId: string) => {
  const formElement = document.getElementById(formId);
  if (formElement) {
    formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }
};

const displayErrorMessageOnBlur = (meta: FieldMetaState<any>) => {
  const { error, submitting, touched, data } = meta;
  const { modifiedSinceLastBlur } = data || {};
  if (!error) return false;
  if (submitting) return false;
  if (!touched) return false;
  if (modifiedSinceLastBlur) return false;

  return true;
};

export const getErrorMessage = (meta: IFormElementProps['meta']): string => {
  const emptyString = '';
  if (!meta) return emptyString;

  if (!displayErrorMessageOnBlur(meta)) {
    return emptyString;
  }

  const { error } = meta;

  if (isString(error)) return i18n.t(error);

  if ('error' in error) {
    return i18n.t(error.error);
  }

  const { key, params } = error || {};
  return i18n.t(key, { ...(params || {}) });
};

export const parseErrorMessageToFieldBox = (error: string): string => {
  const ONE_SPACE_CHAR = ' ';
  /*
  in case of an error we return a space with one char -
  in order to render the field box props with the error styling,
  error message is rendered inside the form element wrapper
  */
  return error ? ONE_SPACE_CHAR : '';
};

export const getFieldNameWithPrefix = (prefix = '', name: string) => {
  return !prefix ? name : `${prefix}.${name}`;
};

export const parseFieldErrorToBorderError = (
  fieldName: string,
  meta: FieldMetaState<any>
): ErrorResult => {
  const fieldError = meta?.error as ErrorResult;
  if (isObject(fieldError) && 'field' in fieldError) {
    return fieldError.field === fieldName ? getErrorMessage(meta) : '';
  }
  return getErrorMessage(meta);
};

export const onChangeFormElementComponent = <T>(
  { value, secondParam }: { value: T; secondParam?: any },
  params: {
    input: FieldInputProps<T>;
    setModifiedSinceLastBlur?: (...args: any[]) => void;
    onChange?: (value: T, secondParam?: any) => void;
    emptyValueFunc?: (value: T) => boolean;
    enforceSetModifiedSinceLastBlur?: boolean;
  }
) => {
  const {
    input,
    setModifiedSinceLastBlur,
    onChange,
    emptyValueFunc,
    enforceSetModifiedSinceLastBlur
  } = params;
  input.onChange(value);

  if (setModifiedSinceLastBlur) {
    setModifiedSinceLastBlur(
      input.name,
      isBoolean(enforceSetModifiedSinceLastBlur)
        ? enforceSetModifiedSinceLastBlur
        : !(
            (emptyValueFunc
              ? emptyValueFunc(value)
              : typeof value === 'string' && value === '') ||
            typeof value === 'undefined' ||
            value === null
          )
    );
  }
  if (onChange) {
    onChange(value, secondParam);
  }
};
